import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { teamApi } from '../../services/api';
import { Plus, Users, Loader, AlertTriangle, Edit, Trash2, Shield } from 'lucide-react';

const SPORT_EMOJI = { Baseball: '⚾', Softball: '🥎', Soccer: '⚽', Football: '🏈', Basketball: '🏀' };

const TeamListPage = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch (e) { window.scrollTo(0, 0); }
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this team? This action cannot be undone.')) {
      try {
        await teamApi.deleteTeam(id);
        setTeams(teams.filter((team) => team._id !== id));
      } catch (err) {
        setError('Failed to delete team. Please try again.');
        console.error(err);
      }
    }
  };

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const response = await teamApi.getTeams();
        setTeams(response.data);
      } catch (err) {
        setError('Failed to fetch teams. Please make sure you are logged in and try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800">

      {/* Dark hero header */}
      <div className="bg-gradient-to-r from-gray-900 via-blue-950 to-gray-900 border-b-4 border-yellow-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-full bg-yellow-400 flex items-center justify-center shadow-lg">
                  <Shield className="h-5 w-5 text-gray-900" />
                </div>
                <span className="text-yellow-400 font-bold text-sm uppercase tracking-widest">My Teams</span>
              </div>
              <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight">Team Roster Manager</h1>
              <p className="text-blue-300 mt-1 text-sm">{teams.length} team{teams.length !== 1 ? 's' : ''} in your roster</p>
            </div>
            <Link
              to="/teams/new"
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-gray-900 font-black py-3 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-105 shrink-0"
            >
              <Plus className="h-5 w-5" />New Team
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-royal-blue to-blue-400 flex items-center justify-center mx-auto mb-4 shadow-xl">
                <Loader className="h-7 w-7 text-white animate-spin" />
              </div>
              <p className="text-gray-400 font-medium">Loading teams...</p>
            </div>
          </div>

        ) : error ? (
          <div className="bg-red-900/30 border border-red-500/30 rounded-2xl p-5 flex items-center gap-3 text-red-400">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <span className="text-sm font-medium">{error}</span>
          </div>

        ) : teams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-20 h-20 rounded-full bg-yellow-400 flex items-center justify-center shadow-2xl mb-6">
              <Shield className="h-10 w-10 text-gray-900" />
            </div>
            <h3 className="text-2xl font-black text-white mb-2">No Teams Yet</h3>
            <p className="text-gray-400 text-center max-w-sm mb-8">Create your first team to get started with AI-powered GameCasts.</p>
            <Link
              to="/teams/new"
              className="inline-flex items-center gap-2 bg-royal-blue hover:bg-blue-800 text-white font-black py-3 px-8 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Plus className="h-5 w-5" />Create Your First Team
            </Link>
          </div>

        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {teams.map((team) => (
              <div key={team._id} className="group bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl hover:shadow-2xl hover:-translate-y-1 hover:from-blue-800/70 hover:border-blue-500/50 transition-all duration-300 overflow-hidden flex flex-col">
                {/* Top accent */}
                <div className="h-1.5 w-full bg-gradient-to-r from-royal-blue to-electric-green" />

                <div className="p-5 flex-grow">
                  {/* Header */}
                  <div className="flex items-start gap-3 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-white/10 flex items-center justify-center shrink-0 text-xl">
                      {SPORT_EMOJI[team.sport] || '🏆'}
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-black text-white text-lg leading-tight group-hover:text-yellow-300 transition-colors">{team.teamName}</h3>
                      <span className="text-xs font-semibold text-blue-300 uppercase tracking-wide">{team.sport}</span>
                    </div>
                  </div>

                  {/* Roster preview */}
                  <div>
                    <div className="flex items-center gap-1.5 mb-2">
                      <Users className="h-3.5 w-3.5 text-gray-400" />
                      <span className="text-xs font-bold text-gray-400 uppercase tracking-wide">{team.roster.length} Players</span>
                    </div>
                    <div className="space-y-1">
                      {team.roster.slice(0, 5).map((player, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-gray-300">
                          <span className="w-6 h-6 rounded-md bg-white/10 flex items-center justify-center text-xs font-bold text-gray-400 shrink-0">
                            {player.playerNumber || '—'}
                          </span>
                          <span className="truncate">{player.playerName}</span>
                          {player.position && <span className="text-xs text-gray-400 ml-auto shrink-0">{player.position}</span>}
                        </div>
                      ))}
                      {team.roster.length > 5 && (
                        <p className="text-xs text-gray-500 pl-8">+{team.roster.length - 5} more players</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Footer actions */}
                <div className="flex items-center justify-end gap-2 border-t border-white/10 px-5 py-3 bg-white/5">
                  <Link
                    to={`/teams/edit/${team._id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-300 hover:text-white bg-white/10 hover:bg-blue-500/30 border border-white/20 hover:border-blue-400/50 px-3 py-1.5 rounded-lg transition-all duration-200"
                  >
                    <Edit className="h-3.5 w-3.5" />Edit
                  </Link>
                  <button
                    onClick={() => handleDelete(team._id)}
                    className="inline-flex items-center gap-1.5 text-xs font-bold text-gray-400 hover:text-red-400 bg-white/10 hover:bg-red-500/20 border border-white/20 hover:border-red-500/40 px-3 py-1.5 rounded-lg transition-all duration-200"
                  >
                    <Trash2 className="h-3.5 w-3.5" />Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamListPage;
